import { useQuery } from '@redwoodjs/web'

import { GET_CUSTOM_FIELDS } from 'src/graphql/customFields.graphql'

import { ApplicantFiltersActionType } from '../../../context/applicant-filters.reducer'
import {
  useFilters,
  useFiltersDispatch,
} from '../../../context/ApplicantFiltersContext'

/**
 * Hook for managing custom field filters
 * Provides searchable custom fields and handles filter state changes
 */
export const useCustomFieldsFilter = () => {
  const filters = useFilters()
  const dispatch = useFiltersDispatch()

  // Fetch searchable custom fields
  const { data } = useQuery(GET_CUSTOM_FIELDS, {
    variables: {
      status: 'ACTIVE',
      pageInfo: { limit: 100, offset: 0 }, // Get all fields (max 100)
    },
  })

  const searchableFields = (data?.customFields?.items || [])
    .filter((f) => f.isSearchable)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  const selectedValues = filters.customFields || []

  const handleChange = (customFieldId: string, value: any) => {
    const existingIndex = selectedValues.findIndex(
      (f) => f.customFieldId === customFieldId
    )
    let newValues = [...selectedValues]

    // Check if value is empty (null, undefined, empty string, empty array, or empty date object)
    const isEmpty =
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      // Check for empty date objects where both gte and lte are null/undefined
      (value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        (value.gte !== undefined || value.lte !== undefined) &&
        (value.gte === null || value.gte === undefined) &&
        (value.lte === null || value.lte === undefined))

    if (isEmpty) {
      // Remove filter
      if (existingIndex >= 0) {
        newValues.splice(existingIndex, 1)
      }
    } else {
      // Add or update filter
      const field = searchableFields.find(
        (f) => f.customFieldId === customFieldId
      )
      if (!field) {
        console.error('Field not found for customFieldId:', customFieldId)
        return
      }

      const filterValue = buildFilterValue(field.fieldType, value)

      if (existingIndex >= 0) {
        newValues[existingIndex] = { customFieldId, ...filterValue }
      } else {
        newValues.push({ customFieldId, ...filterValue })
      }
    }

    dispatch({
      type: ApplicantFiltersActionType.CUSTOM_FIELDS_CHANGED,
      payload: newValues,
    })
  }

  const handleClear = (customFieldId: string) => {
    const newValues = selectedValues.filter(
      (f) => f.customFieldId !== customFieldId
    )
    dispatch({
      type: ApplicantFiltersActionType.CUSTOM_FIELDS_CHANGED,
      payload: newValues,
    })
  }

  const handleClearAll = () => {
    // Clear all custom field filters in a single dispatch
    // This avoids stale closure issues when clearing multiple filters
    dispatch({
      type: ApplicantFiltersActionType.CUSTOM_FIELDS_CHANGED,
      payload: [],
    })
  }

  return {
    searchableFields,
    selectedValues,
    handleChange,
    handleClear,
    handleClearAll,
    totalSelectedCount: selectedValues.length,
    isLoaded: !data || data.customFields !== undefined,
    loading: !data,
  }
}

/**
 * Build filter value object based on field type
 */
const buildFilterValue = (fieldType: string, value: any) => {
  switch (fieldType) {
    case 'STRING':
      return { stringValue: value }
    case 'BOOLEAN':
      return { booleanValue: value }
    case 'DATE':
      return { dateValue: value }
    case 'SINGLE_SELECT':
      return { selectValues: Array.isArray(value) ? value : [value] }
    case 'MULTI_SELECT':
      return { selectValues: Array.isArray(value) ? value : [value] }
    default:
      return {}
  }
}
